import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { GitFork, ArrowRight } from 'lucide-react';
import type { TemplateTreeNode } from '../../types/template.types';

interface ForkDialogProps {
  open: boolean;
  onClose: () => void;
  baseTemplates: TemplateTreeNode[];
  onFork: (sourceTemplateId: string, name?: string) => void;
  isForking?: boolean;
}

export default function ForkDialog({ open, onClose, baseTemplates, onFork, isForking }: ForkDialogProps) {
  const [sourceId, setSourceId] = useState('');
  const selectedSource = baseTemplates.find(t => t.id === sourceId);

  const handleFork = () => {
    if (!sourceId) return;
    onFork(sourceId, selectedSource?.name);
    setSourceId('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <GitFork className="h-4 w-4" />
            Fork Template
          </DialogTitle>
          <DialogDescription className="text-xs">
            Create a tenant-specific copy of a base template.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Source Template</label>
            <Select value={sourceId} onValueChange={setSourceId}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select base template..." />
              </SelectTrigger>
              <SelectContent>
                {baseTemplates.map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-sm">
                    {t.path}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-center">
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </div>

          {selectedSource && (
            <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">This will:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Copy <span className="font-mono">{selectedSource.path}</span> into the tenant folder</li>
                <li>Create an initial commit "fork from base"</li>
                <li>Set as the active template for this tenant</li>
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs">Cancel</Button>
          <Button size="sm" disabled={!sourceId || isForking} onClick={handleFork} className="text-xs gap-1.5">
            <GitFork className="h-3 w-3" />
            {isForking ? 'Forking...' : 'Fork & Edit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
